import React, { useMemo, useState } from "react";
import { ArrowLeft, FileText, Save } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const AddProgressReport: React.FC = () => {
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      // expected: topics/approval/detail/:id/progress/add → last meaningful is id at index length-3
      return parts[parts.length - 3] || "";
    } catch {
      return "";
    }
  }, []);

  const [form, setForm] = useState({
    month: "",
    year: "",
    reportContent: "",
    nextMonthPlan: "",
    attachedFiles: [] as File[],
  });

  const change = (field: string, value: string | File[]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      change("attachedFiles", [...form.attachedFiles, ...newFiles]);
    }
  };

  const months = [
    "Tháng 1",
    "Tháng 2",
    "Tháng 3",
    "Tháng 4",
    "Tháng 5",
    "Tháng 6",
    "Tháng 7",
    "Tháng 8",
    "Tháng 9",
    "Tháng 10",
    "Tháng 11",
    "Tháng 12",
  ];

  const years = Array.from({ length: 10 }, (_, i) => `${2020 + i}`);

  return (
    <PageShell title="Thêm báo cáo tiến độ">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => {
                // Navigate back to project detail with progress tab active
                const projectId = window.location.pathname.split("/")[4]; // Extract project ID from URL
                window.location.href = `/topics/approval/detail/${projectId}`;
              }}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <FileText className="h-5 w-5 text-[#337ab7]" />
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Thêm báo cáo tiến độ
            </h1>
          </div>
        </div>
        {/* Topic Title */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-blue-800">Đề tài:</h2>
            <p className="text-sm text-blue-700">
              Nghiên cứu xác định nguyên nhân và đề xuất giải pháp khoa học công
              nghệ nhằm ổn định các cửa sông và vùng bờ biển tỉnh Bình Thuận
            </p>
          </div>
        </div>

        <div className="w-full p-6 space-y-6">
          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tháng <span className="text-red-500">(*)</span>
              </label>
              <select
                value={form.month}
                onChange={(e) => change("month", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn -</option>
                {months.map((month, index) => (
                  <option key={index} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Năm <span className="text-red-500">(*)</span>
              </label>
              <select
                value={form.year}
                onChange={(e) => change("year", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn -</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content Fields */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nội dung báo cáo
              </label>
              <textarea
                placeholder="Nhập nội dung báo cáo..."
                value={form.reportContent}
                onChange={(e) => change("reportContent", e.target.value)}
                className="w-full h-64 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kế hoạch hoạt động của tháng tiếp theo
              </label>
              <textarea
                placeholder="Nhập kế hoạch hoạt động..."
                value={form.nextMonthPlan}
                onChange={(e) => change("nextMonthPlan", e.target.value)}
                className="w-full h-64 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* File Upload */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File đính kèm
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                  id="progress-file-upload"
                />
                <label
                  htmlFor="progress-file-upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  Choose Files
                </label>
                <span className="text-sm text-gray-500">
                  {form.attachedFiles.length === 0
                    ? "No file chosen"
                    : `${form.attachedFiles.length} file(s) chosen`}
                </span>
              </div>

              {form.attachedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Files:
                  </h4>
                  <div
                    className={`space-y-2 ${
                      form.attachedFiles.length > 3
                        ? "max-h-48 overflow-y-auto pr-2"
                        : ""
                    }`}
                  >
                    {form.attachedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const updatedFiles = form.attachedFiles.filter(
                              (_, i) => i !== index
                            );
                            change("attachedFiles", updatedFiles);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                          title="Xóa file"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              onClick={() => window.history.back()}
            >
              Hủy
            </button>
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium shadow-sm inline-flex items-center gap-2"
              onClick={() => {
                console.log("Save progress report", form);
                // Navigate back to project detail with progress tab active
                const projectId = window.location.pathname.split("/")[4]; // Extract project ID from URL
                window.location.href = `/topics/approval/detail/${projectId}`;
              }}
            >
              <Save className="h-4 w-4" />
              <span>Lưu dữ liệu</span>
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AddProgressReport;
